import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get, sortBy } from 'lodash';
import {
  ProgressIndicator,
  SecurityHelper,
  HelmetWrapper,
  SvgLoading,
  CSRFacilityInformation,
  TYPES,
  Box,
  ContentBlock,
  Heading,
  Spacer,
  Stack,
  Text
} from 'sarsaparilla';

import {
  OrderDetailsLayout,
  OrderDetailsTimerNotice,
  OrderDetailsSection,
  OrderDetailsSummary
} from 'shared-ui';

import ErrorNotification from 'ui-permit/shared-components/common/ErrorNotification';
import { updateAccount } from 'ui-account/src/actions/profileActions';
import GroupDetailHOC from 'ui-permit/shared-components/registration/GroupDetail';
import AlternatePermitHolderEntry from 'ui-permit/shared-components/registration/AlternatePermitHolderEntry';
import AlternatePermitHolderHOC from 'ui-permit/shared-components/registration/AlternatePermitHolder';
import CommercialGuideListHOC from 'ui-permit/shared-components/registration/CommercialGuideList';
import TripItineraryHOC from 'ui-permit/shared-components/registration/TripItinerary';
import TravelMethodHOC from 'ui-permit/shared-components/registration/TravelMethod';
import BringAnimalQuestionHOC from 'ui-permit/shared-components/registration/BringAnimalsQuestionHOC';
import AnimalDetailHOC from 'ui-permit/shared-components/registration/AnimalDetail';
import IssueStationDetailHOC from 'ui-permit/shared-components/registration/IssueStationDetail';
import NeedToKnowHOC from 'ui-permit/shared-components/registration/NeedToKnow';
import { loadFeesScript, setButtonLabels } from 'ui-permit/src/utils/utils';

import * as types from 'ui-permit/src/constants';
import {
  GROUP_DETAIL_PROP_PATH,
  GROUP_DETAIL_ADDRESS_PROP_PATH,
  GROUP_DETAIL_FIRST_NAME_PROP_PATH,
  GROUP_DETAIL_LAST_NAME_PROP_PATH,
  GROUP_DETAIL_MIDDLE_NAME_PROP_PATH,
  GROUP_DETAIL_ZIPCODE_PROP_PATH,
  GROUP_DETAIL_ADDRESS1_PROP_PATH,
  GROUP_DETAIL_ADDRESS2_PROP_PATH,
  GROUP_DETAIL_STATE_PROP_PATH,
  GROUP_DETAIL_CITY_PROP_PATH,
  GROUP_DETAIL_EMAIL_PROP_PATH,
  GROUP_DETAIL_PHONE_PROP_PATH,
  GROUP_DETAIL_ALT_PROP_PATH,
  GROUP_INFO_PROP_PATH,
  GROUP_LEADERS_GUIDES_PROP_PATH,
  CAMPSITE_PREFERENCE_PROP_PATH,
  ENTRY_LOCATION_ID_PROP_PATH,
  EXIT_LOCATION_ID_PROP_PATH,
  SELECTED_GROUP_SIZE_PROP_PATH,
  GROUP_SIZE_PROP_PATH,
  TRAVEL_METHOD_TYPE_PROP_PATH,
  IS_BRINGING_ANIMALS_PROP_PATH,
  ANIMAL_DETAILS_PROP_PATH,
  PICKUP_LOCATION_PROP_PATH,
  COMMENTS_PROP_PATH,
  NEED_TO_KNOW_CHECKED_PROP_PATH,
} from 'ui-permit/shared-components/constants/propPaths';
import { fetchReservationSummaryImageUrl } from 'ui-permit/shared-components/actions/actions';

import ModifyGroupSizeModalContainer from './ModifyGroupSizeModalContainer';
import PermitShape from '../utils/permitShape';
import issuanceShape from '../utils/issuanceShape';
import ReservationDetail from '../components/registration/ReservationDetail';
import * as permitActions from '../actions/permit';
import {
  MT_WHITNEY_PERMIT_ID,
  MTWHITNEY_VISIT_TRAILS,
  INYO_NF_PERMIT_ID,
  calculateFeesInyo,
} from '../utils/inyoUtils';
import { issuanceStatuses, modifyIssuanceGroupSize } from '../utils/permitUtils';
import ValidationError from '../components/registration/ValidationError';
import { selectors as sharedSelectors } from '../reducers/shared';

const propTypes = {
  history: TYPES.HISTORY,
  match: TYPES.MATCH,
  dispatch: PropTypes.func,
  fetchError: PropTypes.shape({}),
  cartError: PropTypes.shape({}),
  permit: PermitShape,
  inyoValidation: PropTypes.shape({}),
  issuance: issuanceShape,
  permitCampsites: PropTypes.arrayOf(PropTypes.object),
  postIssuanceInProgress: PropTypes.bool,
  divisionCampsites: PropTypes.arrayOf(PropTypes.object),
  overnightCampsites: PropTypes.arrayOf(PropTypes.object),
  accountEmployees: PropTypes.array,
  imageUrl: PropTypes.string,
};

const databindings = {
  [GROUP_DETAIL_PROP_PATH]: 'permit_holder',
  [GROUP_DETAIL_ADDRESS_PROP_PATH]: 'permit_holder_address',
  [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
  [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
  [GROUP_DETAIL_MIDDLE_NAME_PROP_PATH]: 'middle_name',
  [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'postal_code',
  [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'street_address1',
  [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'street_address2',
  [GROUP_DETAIL_STATE_PROP_PATH]: 'state_code',
  [GROUP_DETAIL_CITY_PROP_PATH]: 'city',
  [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
  [GROUP_DETAIL_PHONE_PROP_PATH]: 'phone',
  [GROUP_DETAIL_ALT_PROP_PATH]: 'alt_group_leaders',
  [GROUP_INFO_PROP_PATH]: 'group_info',
  [GROUP_LEADERS_GUIDES_PROP_PATH]: 'guides',
  [CAMPSITE_PREFERENCE_PROP_PATH]: 'campsite_preferences',
  [ENTRY_LOCATION_ID_PROP_PATH]: 'entry_location_id',
  [EXIT_LOCATION_ID_PROP_PATH]: 'exit_location_id',
  [SELECTED_GROUP_SIZE_PROP_PATH]: 'selected_group_size',
  [GROUP_SIZE_PROP_PATH]: 'group_size',
  [TRAVEL_METHOD_TYPE_PROP_PATH]: 'travel_method_type',
  [IS_BRINGING_ANIMALS_PROP_PATH]: 'is_bringing_animals',
  [ANIMAL_DETAILS_PROP_PATH]: 'animals',
  [PICKUP_LOCATION_PROP_PATH]: 'pickup_location_id',
  [COMMENTS_PROP_PATH]: 'comment',
  [NEED_TO_KNOW_CHECKED_PROP_PATH]: 'need_to_know_checked'
};

const groupDetail = GroupDetailHOC(
  issuanceShape,
  databindings
  // the component default suffices
);

const alternatePermitHolder = AlternatePermitHolderHOC(
  AlternatePermitHolderEntry,
  issuanceShape,
  databindings
);

const commercialGuideList = CommercialGuideListHOC(
  issuanceShape,
  databindings
);

const tripItinerary = TripItineraryHOC(
  issuanceShape,
  databindings
);

const travelMethod = TravelMethodHOC(
  issuanceShape,
  databindings
)

const bringAnimalQuestion = BringAnimalQuestionHOC(
  issuanceShape,
  databindings
);

const animalDetail = AnimalDetailHOC(
  issuanceShape,
  databindings
);

const issueStationDetail = IssueStationDetailHOC(
  issuanceShape,
  databindings
);

const needToKnow = NeedToKnowHOC(
  issuanceShape,
  databindings
)

const componentMapping = {
  [INYO_NF_PERMIT_ID]: [
    groupDetail,
    alternatePermitHolder,
    commercialGuideList,
    tripItinerary,
    travelMethod,
    bringAnimalQuestion,
    animalDetail,
    issueStationDetail,
    needToKnow
  ],
  [MT_WHITNEY_PERMIT_ID]: [
    groupDetail,
    alternatePermitHolder,
    commercialGuideList,
    tripItinerary,
    travelMethod,
    issueStationDetail,
    needToKnow
  ]
};

class PermitRegistrationPage extends React.Component {
  state = {
    isModifyGroupSizeModalOpen: false,
    maxGroupSize: 0,
    isLoadingReservation: true,
  }

  async componentDidMount() {
    // fetch first issuance from the list of reservationids and initialize
    // this page allows bulk editing of "initial" issuances (if there are multiple)
    const firstReservationId = this.props.match.params.reservationIds.split(',')[0];
    const issuanceId = firstReservationId.split(types.RES_ID_DELIMITER)[0];
    const permitId = this.props.match.params.permitId;
    // fetch permit and recreation area
    this.props.pActions.fetchPermit(permitId)
      .then(async () => {
        const recId = get(this.props.permit, 'recareas[0]', '0');
        if (recId !== '0') {
          this.props.pActions.fetchRecarea(recId);
        }

        // fetch issuance
        const issuance = await this.props.pActions.fetchUnpaidIssuance(issuanceId, permitId)
        this.setState({ maxGroupSize: issuance.group_size });
        await Promise.all([
          this.props.pActions.fetchCampsites(permitId),
          this.props.pActions.fetchDivisionCampsites(permitId, issuance.division_id)
        ]);
        this.setState({ isLoadingReservation: false });
      });
    this.props.fetchReservationSummaryImageUrl(permitId);
    // fetch what's in user's cart.
    this.props.pActions.fetchCart();
    if (SecurityHelper.isCommercialAccount()) {
      this.props.pActions.fetchAccountEmployees();
    }

    await loadFeesScript();
    this.forceUpdate();
  }

  getValidationErrorBullets = (validation) => {
    const summaries = sortBy(get(validation, 'summary', []), "path");
    return summaries.map((summary) => {
      return { ...summary.error, key: summary.path };
    });
  }

  isThisFormValid = () => {
    const { summary } = this.props.inyoValidation;
    return summary.length === 0;
  }

  createUpdatedAccountObject = (prevAcct) => {
    const { issuance } = this.props;

    const first_name = get(issuance, `${databindings[GROUP_DETAIL_PROP_PATH]}.first_name`, '');
    const last_name = get(issuance, `${databindings[GROUP_DETAIL_PROP_PATH]}.last_name`, '');
    const email = get(issuance, `${databindings[GROUP_DETAIL_PROP_PATH]}.email`, '');
    const cell_phone = get(issuance, `${databindings[GROUP_DETAIL_PROP_PATH]}.phone`, '');

    const zip_code = get(issuance, `${databindings[GROUP_DETAIL_ADDRESS_PROP_PATH]}.postal_code`, '');
    const address1 = get(issuance, `${databindings[GROUP_DETAIL_ADDRESS_PROP_PATH]}.street_address1`, '');
    const state = get(issuance, `${databindings[GROUP_DETAIL_ADDRESS_PROP_PATH]}.state_code`, '');
    const city = get(issuance, `${databindings[GROUP_DETAIL_ADDRESS_PROP_PATH]}.city`, '');

    const home_address = { zip_code, address1, state, city };

    const acctObj = { ...prevAcct, first_name, last_name, home_address, email, cell_phone };
    return acctObj;
  }

  saveLeaderInfo = () => {
    const acct = this.createUpdatedAccountObject(SecurityHelper.getAccount());
    this.props.updateAccount(acct);
  }

  addToCart = () => {
    const { pActions, permit, issuance } = this.props;
    if (!this.isThisFormValid()) {
      pActions.setDisplayValidationErros();
      window.scroll(0, 0);
      return;
    }

    if (get(issuance, 'permit_holder.save_profile')) {
      this.saveLeaderInfo()
    }

    if (issuance.status === issuanceStatuses.get(types.PERMIT_PRELIMINARY)) {
      issuance.status = issuanceStatuses.get(types.PERMIT_IN_PROGRESS);
    }

    pActions.updateIssuance(issuance).then((updatedIssuance) => {
        const { updateIssuanceResult } = this.props;
        if (updateIssuanceResult === types.ISSUANCE_UPDATE_REQUIRES_PAYMENT) {
          window.location.assign("/cart");
        }
        if (updateIssuanceResult === types.ISSUANCE_UPDATE_REQUIRES_REFUND) {
          pActions.bypassToOrderConfirmation(updatedIssuance, permit);
          this.props.history.push(`/permits/${permit.id}/confirmation`);
        }
        if (updateIssuanceResult === types.ISSUANCE_UPDATE_SUCCESSFUL) {
          window.location.assign(`/account/orders/${issuance.order_id}/reservations/${issuance.id}__${permit.id}`);
        }
    });
  }

  continueShopping = () => {
    window.location.assign('/');
  }

  removeFromCart = () => {
    const issuanceId = get(this.props.issuance, 'id');
    const permitId = get(this.props.permit, 'id');
    if (issuanceId && permitId) {
      const reservationId = `${issuanceId}${types.RES_ID_DELIMITER}${permitId}`;
      this.props.pActions.cancelReservation(reservationId, this.props.history);
    }
  }

  onIncreaseGroupSizeSuccess = (newGroupSize) => {
    this.setState({
      isModifyGroupSizeModalOpen: false,
      maxGroupSize: newGroupSize,
    }, () => {
      this.props.pActions.replacePendingReservation(modifyIssuanceGroupSize(this.props.issuance, newGroupSize))
    });
  };

  closeIncreaseGroupSizeModal = () => {
    this.setState({ isModifyGroupSizeModalOpen: false });
  };

  handleModifyQuota = () => {
    this.setState({ isModifyGroupSizeModalOpen: true });
  };

  getButtonLabels = () => {
    const { issuance, permit } = this.props;
    const mustVisitMtWhitney = MTWHITNEY_VISIT_TRAILS.includes(issuance.division_id);
    const division = get(permit.divisions, `${issuance.division_id}`, {});
    const isVisitingMtWhitney = issuance.is_visiting_mt_whitney;
    const fees = get(division, 'fees', []);

    const inyoFees = calculateFeesInyo(fees, issuance.group_info, issuance.permit_type, (isVisitingMtWhitney || mustVisitMtWhitney));
    const amountDue = inyoFees.total ? inyoFees.total - get(issuance, 'amount_paid', 0) : 0;
    return setButtonLabels(issuance, false, amountDue);
  }

  getDivision() {
    const { issuance: reservation, permit } = this.props;

    if (!permit.hasOwnProperty('divisions')) {
        return { name: permit.name };
    }

    if (reservation.division_id) {
        return permit.divisions[reservation.division_id];
    }

    if (Object.keys(permit.divisions).length) {
      return permit.divisions[Object.keys(permit.divisions)[0]];
    }

    return { name: permit.name };
  }

  renderTimeNotice = () => {
    // Permit complete does not have time limit to fill order details
    if(this.props.issuance.status === issuanceStatuses.get(types.PERMIT_COMPLETE)) {
      return null;
    }
    return <OrderDetailsTimerNotice />
  }

  render() {
    const errors = []
    const {
      accountEmployees,
      updateIssuanceError,
      fetchError,
      cartError,
      pActions,
      issuance,
      inyoValidation,
      match,
      permit,
      permitCampsites,
      divisionCampsites,
      entrancesExits,
      validIssueStations,
      history } = this.props;
    const { permitId } = match.params;
    const account = SecurityHelper.getAccount();

    if (updateIssuanceError) {
      errors.push(updateIssuanceError);
    }
    if (fetchError) {
      errors.push(fetchError);
    }
    if (cartError) {
      errors.push(cartError);
    }

    const buttonsDisabled = issuance.status === issuanceStatuses.get(types.PERMIT_ISSUED) || this.props.postIssuanceInProgress;
    const { displayValidationError } = inyoValidation;
    let props = {
      permit,
      pActions,
      reservation: issuance,
      validation: inyoValidation,
      displayValidationError,
      hasPermission: true,
      entrancesExits,
      account,
      history,
    };
    const formComponents = componentMapping[permitId].map((Component, sindx) => {
      // add extera props to the commerciaal guides list since might be commercial account.
      switch (Component.componentName) {
        case "CommercialGuideList":
          props = { ...props, allGuides: accountEmployees };
          break;
        case "TripItinerary":
          props = {
            ...props,
            permitCampsites,
            divisionCampsites,
            modifyQuotaButtonText: 'Increase Group Size',
            maxGroupSize: this.state.maxGroupSize,
            onModifyQuota: this.handleModifyQuota,
          };
          break;
        case "IssueStationDetail":
          props = { ...props, validIssueStations };
          break;
        case "NeedToKnow":
          props = { ...props };
          break;
        default:
          props = { ...props };
      }
      const key = `order_detail_component_${sindx}`;
      return (<Component {...props} key={key} />);
    });

    if (errors.length === 0 && !issuance.id) {
      return (
        <div className="permit-loading-wrapper">
          <SvgLoading />
        </div>
      );
    }
    const buttonLabels = this.getButtonLabels();

    const summary = (
      <OrderDetailsSummary
        isLoading={this.state.isLoadingReservation}
        heading={this.getDivision()?.name}
        imageUrl={this.props.imageUrl}
        isWaitingForServerResponse={this.state.isLoadingReservation}
        parentName={permit.name}
        isProceedToCartDisabled={buttonsDisabled}
        onProceedToCartClick={this.addToCart}
        proceedToCartButtonText={buttonLabels.proceed}
        secondaryButtonText={buttonLabels.continue}
        onSecondaryButtonClick={this.continueShopping}
      >
        <ReservationDetail
          permit={permit}
          issuance={issuance}
          tripItinerary={this.props.overnightCampsites}
        />
      </OrderDetailsSummary>
    );

    return (
      <div className="inyonwhitney">
        <HelmetWrapper title={`${permit.name} Order Details`} />
        <ModifyGroupSizeModalContainer
          onSuccess={this.onIncreaseGroupSizeSuccess}
          onRequestClose={this.closeIncreaseGroupSizeModal}
          isOpen={this.state.isModifyGroupSizeModalOpen} />
        <Box background="gray-2" paddingY="sm">
          <ProgressIndicator
            steps={['Order Details', 'Review Cart', 'Payment', 'Confirmation']}
            activeStep={1}
          />
        </Box>
        <Spacer size="xl" />
        <ContentBlock>
            <OrderDetailsLayout summaryElement={summary}>
              <ErrorNotification className="permit-error-wrap" errors={errors} />
              {inyoValidation.summary.length > 0 &&
                <ValidationError
                  displayError={displayValidationError}
                  toggle={() => { }}>
                  {[<p key="validation-error-0">
                    Form has <b>{inyoValidation.summary.length}</b> validation errors. Please correct them before you can proceed to payment.
                    </p>, <ul key="validation-errors-detail">{this.getValidationErrorBullets(inyoValidation)}</ul>]}
                </ValidationError>
              }
              <Box>
                <Heading headingLevel={1} appearance="h3">
                    Order Details
                </Heading>
                {permit.name && (
                  <Heading headingLevel={2} appearance="h5" className="permit-name">
                      <Text size="xl" color="gray">{permit.name}</Text>
                  </Heading>
                )}
              </Box>
              {this.renderTimeNotice()}

              <CSRFacilityInformation inventory="Permit" facility_id={this.props.permit.id} />

              <OrderDetailsSection
                heading={permit.name}
                onRemoveClick={this.removeFromCart}
                isLoading={this.state.isLoadingReservation}>
                <div className="permit-content-outer-wrap">
                  <Stack className="permit-content-main" space="lg">
                    {formComponents}
                  </Stack>
                </div>
              </OrderDetailsSection>

            </OrderDetailsLayout>
        </ContentBlock>
      </div>
    );
  }
}

PermitRegistrationPage.displayName = 'PermitRegistrationPage';
PermitRegistrationPage.propTypes = propTypes;

function select(state) {
  return {
    permit: state.inyonwhitney.permit.result,
    issuance: state.inyonwhitney.permit.issuance,
    updatedIssuance: state.inyonwhitney.permit.updated_issuance,
    postIssuanceInProgress: state.inyonwhitney.permit.issuance.post_in_progress,
    updateIssuanceResult: state.inyonwhitney.permit.update_issuance_result,
    updateIssuanceError: state.inyonwhitney.permit.update_issuance_error,
    issuanceError: state.inyonwhitney.permit.fetch_issuance_error,
    recareaError: state.inyonwhitney.permit.recarea_err,
    fetchError: state.inyonwhitney.permit.fetch_err,
    feeDiff: state.inyonwhitney.permit.issuance_fee_difference,
    feeDiffError: state.inyonwhitney.permit.issuance_fee_difference_err,
    inyoValidation: state.inyoValidation,
    cart: state.inyonwhitney.permit.cart,
    cartError: state.inyonwhitney.permit.cart_err,
    filters: state.inyonwhitney.permit.filters,
    selectedEndDate: state.inyonwhitney.selectedEndDate,
    permitCampsites: state.inyonwhitney.permit.campsites,
    divisionCampsites: state.inyonwhitney.permit.entryCampsites,
    overnightCampsites: state.inyonwhitney.permit.overnightCampsites,
    accountEmployees: state.inyonwhitney.account_employees,
    entrancesExits: state.inyonwhitney.permit.entrancesExits,
    validIssueStations: state.inyonwhitney.permit.validIssueStations,
    imageUrl: sharedSelectors.getImageUrlSelector(state),
  };
}

function mapDispatchToProps(dispatch) {
  return ({
    pActions: bindActionCreators(permitActions, dispatch),
    updateAccount: bindActionCreators(updateAccount, dispatch),
    fetchReservationSummaryImageUrl: bindActionCreators(fetchReservationSummaryImageUrl, dispatch)
  });
}

export default connect(select, mapDispatchToProps)(PermitRegistrationPage);
